/**
 * Shared prefill/mutation wiring for opt-in order document modals.
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 */
import { useEffect, useMemo, useState } from "react";

import type { z } from "zod";
import { toOptInOrderDocumentCreateInput } from "~/modules/orders/order-documents/create-modal-input";
import { OptInOrderDocumentCreateInputSchema } from "~/modules/orders/order-menu-document-extensions/opt-in-order-document.schema";
import { shouldHydrateOrderDocumentPrefill } from "~/modules/orders/order-menu-document-extensions/order-document-prefill-hydration";
import type { OrderDocumentSlug } from "~/shared/documents/order-document-settings";
import type { OrderDocumentSourceType } from "~/shared/documents/order-document-source";
import type { CarrierContactPrefill } from "~/shared/documents/schemas/carrier-contact-prefill.schema";
import { emptyCarrierContactPrefill } from "~/shared/documents/schemas/carrier-contact-prefill.schema";
import type { DocumentAttributePrefillCandidates } from "~/shared/documents/schemas/document-attribute-wiring.schema";
import { emptyDocumentAttributePrefillCandidates } from "~/shared/documents/schemas/document-attribute-wiring.schema";
import { useForm, zodResolver } from "~/ui";
import { api } from "~/utils/api";

type UseOptInOrderDocumentFormArgs<TValues extends { orderId: string }> = {
  orderId: string;
  sourceType?: OrderDocumentSourceType;
  slug: OrderDocumentSlug;
  schema: z.ZodType<TValues, z.ZodTypeDef, unknown>;
  initialValues: TValues;
  onSuccess: () => void;
  /** Preserve issuance choices, including an explicitly empty cost selection, across refetches. */
  hydrateOncePerSource?: boolean;
};

export const useOptInOrderDocumentForm = <TValues extends { orderId: string }>({
  orderId,
  sourceType = "order",
  slug,
  schema,
  initialValues,
  onSuccess,
  hydrateOncePerSource = false,
}: UseOptInOrderDocumentFormArgs<TValues>) => {
  const utils = api.useContext();
  const sourceKey = `${slug}:${sourceType}:${orderId.toLowerCase()}`;
  const [hydratedSourceKey, setHydratedSourceKey] = useState<string | null>(null);
  const [attributePrefillCandidates, setAttributePrefillCandidates] = useState<DocumentAttributePrefillCandidates>(
    emptyDocumentAttributePrefillCandidates(),
  );
  const [carrierContactPrefill, setCarrierContactPrefill] = useState<CarrierContactPrefill>(emptyCarrierContactPrefill());
  const prefillQuery = api.orders.getOptInOrderDocumentPrefill.useQuery(
    { id: orderId, sourceType, slug },
    { refetchOnMount: "always", refetchOnWindowFocus: false, refetchOnReconnect: false },
  );
  const mutation = api.orders.createOptInOrderDocument.useMutation({
    onSuccess: ({ pdfUrl }) => {
      void utils.documents.getMany.invalidate();
      const win = window.open(pdfUrl, "_blank", "noopener,noreferrer");
      if (win) {
        win.opener = null;
      }
      onSuccess();
    },
  });

  const prefillResult = useMemo(
    () => (prefillQuery.data ? schema.safeParse({ ...prefillQuery.data.input, orderId, sourceType }) : null),
    [prefillQuery.data, orderId, sourceType, schema],
  );
  const prefillValidationMessage =
    prefillResult && !prefillResult.success
      ? `Could not load document details: ${prefillResult.error.issues[0]?.message ?? "Invalid document data."}`
      : undefined;
  const isPrefillReady =
    !prefillQuery.isFetching &&
    !prefillQuery.isError &&
    prefillResult?.success === true &&
    (!hydrateOncePerSource || hydratedSourceKey === sourceKey);

  // Engine choice is issuance state, separate from the built-in document draft.
  const [useOutputTemplate, setUseOutputTemplate] = useState(false);
  const resolveCreateInput = (values: TValues) => ({
    ...toOptInOrderDocumentCreateInput(values, prefillResult?.success ? prefillResult.data : initialValues, useOutputTemplate),
    orderId,
    sourceType,
    slug,
    useOutputTemplate,
  });
  const form = useForm<TValues>({
    validate: values => zodResolver(OptInOrderDocumentCreateInputSchema)(resolveCreateInput(values)),
    initialValues,
  });

  const { setValues } = form;
  const shouldHydratePrefill = shouldHydrateOrderDocumentPrefill({
    hydrateOncePerSource,
    sourceKey,
    hydratedSourceKey,
    isFetching: prefillQuery.isFetching,
    isError: prefillQuery.isError,
  });
  useEffect(() => {
    if (!prefillQuery.data || !prefillResult?.success || !shouldHydratePrefill) {
      return;
    }
    const { attributePrefillCandidates: candidates, carrierContactPrefill: contactPrefill } = prefillQuery.data;
    setValues(prefillResult.data);
    setAttributePrefillCandidates(candidates);
    setCarrierContactPrefill(contactPrefill);
    if (hydrateOncePerSource) {
      setHydratedSourceKey(sourceKey);
    }
  }, [prefillQuery.data, prefillResult, setValues, hydrateOncePerSource, sourceKey, shouldHydratePrefill]);

  const handleSubmit = (values: TValues) => {
    if (!isPrefillReady) {
      return;
    }
    mutation.mutate(OptInOrderDocumentCreateInputSchema.parse(resolveCreateInput(values)));
  };

  const prefillErrorMessage = prefillQuery.error?.message ?? prefillValidationMessage;
  const prefillFeedback = prefillErrorMessage
    ? [
        {
          message: prefillErrorMessage,
          visible: true,
        },
      ]
    : undefined;

  return {
    form,
    prefillQuery,
    mutation,
    handleSubmit,
    prefillFeedback,
    isPrefillReady,
    attributePrefillCandidates,
    carrierContactPrefill,
    addressCandidates: prefillQuery.data?.addressCandidates ?? [],
    savedFacts: prefillQuery.data?.savedFacts,
    useOutputTemplate,
    setUseOutputTemplate,
  };
};
